import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

// GA is loaded for all hostnames in index.html, but page_view events only fire
// for the production hostname so preview traffic doesn't pollute analytics.
const GA_ID = "G-7BB2DHRNY0";
const GA_HOSTNAMES = new Set(["antera.bp.agency", "anterasoftware.com", "www.anterasoftware.com"]);
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import AccessibilityWidget from "@/components/layout/AccessibilityWidget";
import MobileDemoPill from "@/components/MobileDemoPill";
// HomePage renders through the block-based CMS via BlockRendererPage slug="home".
// The original component is kept on disk as the source of truth for future
// content refreshes; we build the SEO JSON-LD here so the block renderer can
// still include it.
import { organizationJsonLd, softwareApplicationJsonLd } from "@/components/seo/Seo";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [organizationJsonLd, softwareApplicationJsonLd],
};
// SolutionsPage / ProductsPage are imported by the CMS block renderer, not
// directly. The public routes /solutions and /products render via
// <BlockRendererPage slug="solutions"|"products">.
import FeaturesPage from "@/pages/FeaturesPage";
// ExchangePage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="exchange">.
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import WhyPage from "@/pages/WhyPage";
import CareersPage from "@/pages/CareersPage";
import JobDetailPage from "@/pages/JobDetailPage";
import ContactPage from "@/pages/ContactPage";
import AccessibilityPage from "@/pages/AccessibilityPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SecurityPage from "@/pages/SecurityPage";
// CustomerPortalPage is imported by the CMS block renderer, not by App.js
// directly. The public route renders via <BlockRendererPage slug="customer-portal">.
// EventsPage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="events">.
// ApiDocsPage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="developers">.
// OpenHousePage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="openhouse">.
// AcademyPage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="academy">.
// UserGroupsPage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="user-groups">.
// EliteSupplierPage is imported by the CMS block renderer, not by App.js directly.
// The public route renders via <BlockRendererPage slug="elite-supplier">.
// FeaturesPage stays code-driven for now. IntegrationsPage renders through
// the block-based CMS via BlockRendererPage slug="integrations".
import FeatureDetailPage from "@/pages/FeatureDetailPage";
import PresentationsFeaturePage from "@/pages/PresentationsFeaturePage";
import CustomPage from "@/pages/CustomPage";
// Solutions role/product detail pages render through the block-based CMS
// using the composite `solution_detail_page` block. Verbatim content lives
// in the DB and is editable from /admin/site-pages/<slug>. The original
// .jsx components remain on disk as the source-of-truth for the pre-commit
// verbatim guard.
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CaseStudyDetailPage from "@/pages/CaseStudyDetailPage";
import NotFoundPage from "@/pages/NotFoundPage";
import CatchAllRoute from "@/pages/CatchAllRoute";

// Admin
import { AuthProvider } from "@/admin/AuthContext";
import RequireAdmin from "@/admin/RequireAdmin";
import AdminLoginPage from "@/admin/AdminLoginPage";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminLeadsPage from "@/admin/AdminLeadsPage";
import AdminApplicationsPage from "@/admin/AdminApplicationsPage";
import AdminSettingsPage from "@/admin/AdminSettingsPage";
import AdminSiteSettingsPage from "@/admin/AdminSiteSettingsPage";
import AdminBlogPage from "@/admin/AdminBlogPage";
import AdminBlogEditPage from "@/admin/AdminBlogEditPage";
import AdminCaseStudiesPage from "@/admin/AdminCaseStudiesPage";
import AdminCaseStudyEditPage from "@/admin/AdminCaseStudyEditPage";
import AdminJobsPage from "@/admin/AdminJobsPage";
import AdminJobEditPage from "@/admin/AdminJobEditPage";
import AdminSearchAnalyticsPage from "@/admin/AdminSearchAnalyticsPage";
import AdminMediaPage from "@/admin/AdminMediaPage";
import AdminFeaturesPage from "@/admin/AdminFeaturesPage";
import AdminFeatureEditPage from "@/admin/AdminFeatureEditPage";
import AdminPagesPage from "@/admin/AdminPagesPage";
import AdminPageEditPage from "@/admin/AdminPageEditPage";
import AdminTeamPage from "@/admin/AdminTeamPage";
import AdminTeamEditPage from "@/admin/AdminTeamEditPage";
import AdminWhyPageEditor from "@/admin/AdminWhyPageEditor";
import AdminSitePagesPage from "@/admin/AdminSitePagesPage";
import AdminSitePageEditor from "@/admin/AdminSitePageEditor";
import AdminBlockLibraryPage from "@/admin/AdminBlockLibraryPage";
import AdminNavigationPage from "@/admin/AdminNavigationPage";
import AdminTutorialPage from "@/admin/AdminTutorialPage";
import DraftPreviewPage from "@/pages/DraftPreviewPage";
import BlockRendererPage from "@/components/blocks/BlockRenderer";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Use instant scroll so navigation always lands at top (overrides global smooth-scroll CSS)
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function GoogleAnalyticsTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Don't track admin pages
    if (pathname.startsWith("/admin")) return;
    // Don't pollute production analytics with preview/local traffic
    if (typeof window === "undefined") return;
    if (!GA_HOSTNAMES.has(window.location.hostname)) return;
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA_ID,
    });
  }, [pathname, search]);
  return null;
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <AccessibilityWidget />
      <MobileDemoPill />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <GoogleAnalyticsTracker />
        <Routes>
          {/* ---- Admin routes (no public header/footer) ---- */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="leads" element={<AdminLeadsPage />} />
            <Route path="applications" element={<AdminApplicationsPage />} />
            <Route path="site-settings" element={<AdminSiteSettingsPage />} />
            <Route path="blog" element={<AdminBlogPage />} />
            <Route path="blog/:slug" element={<AdminBlogEditPage />} />
            <Route path="case-studies" element={<AdminCaseStudiesPage />} />
            <Route path="case-studies/:slug" element={<AdminCaseStudyEditPage />} />
            <Route path="jobs" element={<AdminJobsPage />} />
            <Route path="jobs/:slug" element={<AdminJobEditPage />} />
            <Route path="features" element={<AdminFeaturesPage />} />
            <Route path="features/:slug" element={<AdminFeatureEditPage />} />
            <Route path="pages" element={<AdminPagesPage />} />
            <Route path="pages/:slug" element={<AdminPageEditPage />} />
            <Route path="team" element={<AdminTeamPage />} />
            <Route path="team/:memberId" element={<AdminTeamEditPage />} />
            <Route path="why-page" element={<AdminWhyPageEditor />} />
            <Route path="site-pages" element={<AdminSitePagesPage />} />
            <Route path="site-pages/:slug" element={<AdminSitePageEditor />} />
            <Route path="navigation" element={<AdminNavigationPage />} />
            <Route path="tutorial" element={<AdminTutorialPage />} />
            <Route path="block-library" element={<AdminBlockLibraryPage />} />
            <Route path="search-analytics" element={<AdminSearchAnalyticsPage />} />
            <Route path="media" element={<AdminMediaPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>

          {/* ---- Public routes ---- */}
          {/*
            Feb 2026 — Public routes render the ORIGINAL hand-written page
            components (verbatim client-approved copy). The block-based CMS
            still exists at /admin/site-pages/<slug> for future editing, but
            we do NOT swap public traffic onto it until a page has been
            explicitly verified and approved by the client.

            Pages that never had a hand-written .jsx file (advance, aether,
            print-shops, sign-companies, quickbooks, developers) continue to
            render via BlockRendererPage because those are the only source of
            their copy.
          */}
          <Route path="/" element={<PublicShell><BlockRendererPage slug="home" testId="home-page" jsonLd={homeJsonLd} /></PublicShell>} />
          <Route path="/solutions" element={<PublicShell><BlockRendererPage slug="solutions" testId="solutions-page" /></PublicShell>} />
          <Route path="/solutions/distributors" element={<PublicShell><BlockRendererPage slug="solutions-distributors" testId="distributors-page" /></PublicShell>} />
          <Route path="/solutions/suppliers" element={<PublicShell><BlockRendererPage slug="solutions-suppliers" testId="suppliers-page" /></PublicShell>} />
          <Route path="/solutions/decorators" element={<PublicShell><BlockRendererPage slug="solutions-decorators" testId="decorators-page" /></PublicShell>} />
          <Route path="/solutions/print-shops" element={<PublicShell><BlockRendererPage slug="solutions-print-shops" testId="print-shops-page" /></PublicShell>} />
          <Route path="/solutions/advance" element={<Navigate to="/advance" replace />} />
          <Route path="/solutions/aether" element={<Navigate to="/aether" replace />} />
          <Route path="/advance" element={<PublicShell><BlockRendererPage slug="advance" testId="advance-page" /></PublicShell>} />
          <Route path="/aether" element={<PublicShell><BlockRendererPage slug="aether" testId="aether-page" /></PublicShell>} />
          <Route path="/solutions/sign-companies" element={<PublicShell><BlockRendererPage slug="solutions-sign-companies" testId="sign-companies-page" /></PublicShell>} />
          <Route path="/solutions/quickbooks" element={<PublicShell><BlockRendererPage slug="solutions-quickbooks" testId="quickbooks-page" /></PublicShell>} />
          <Route path="/integrations" element={<PublicShell><BlockRendererPage slug="integrations" testId="integrations-page" /></PublicShell>} />
          <Route path="/exchange" element={<PublicShell><BlockRendererPage slug="exchange" testId="exchange-page" /></PublicShell>} />
          <Route path="/exchange/user-groups" element={<PublicShell><BlockRendererPage slug="user-groups" testId="user-groups-page" /></PublicShell>} />
          <Route path="/exchange/elite-supplier" element={<PublicShell><BlockRendererPage slug="elite-supplier" testId="elite-supplier-page" /></PublicShell>} />
          <Route path="/products" element={<PublicShell><BlockRendererPage slug="products" testId="products-page" /></PublicShell>} />
          <Route path="/plans" element={<PublicShell><BlockRendererPage slug="plans" testId="plans-page" /></PublicShell>} />
          <Route path="/features" element={<PublicShell><FeaturesPage /></PublicShell>} />
          <Route path="/features/presentations" element={<PublicShell><PresentationsFeaturePage /></PublicShell>} />
          <Route path="/features/:slug" element={<PublicShell><FeatureDetailPage /></PublicShell>} />
          <Route path="/p/:slug" element={<PublicShell><CustomPage /></PublicShell>} />

          <Route path="/blog" element={<PublicShell><BlogPage /></PublicShell>} />
          <Route path="/blog/:slug" element={<PublicShell><BlogPostPage /></PublicShell>} />
          <Route path="/why" element={<PublicShell><WhyPage /></PublicShell>} />
          <Route path="/careers" element={<PublicShell><CareersPage /></PublicShell>} />
          <Route path="/careers/:slug" element={<PublicShell><JobDetailPage /></PublicShell>} />
          <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />
          <Route path="/accessibility" element={<PublicShell><BlockRendererPage slug="accessibility" testId="accessibility-page" /></PublicShell>} />
          <Route path="/customer-portal" element={<PublicShell><BlockRendererPage slug="customer-portal" testId="customer-portal-page" /></PublicShell>} />
          <Route path="/events" element={<PublicShell><BlockRendererPage slug="events" testId="events-page" /></PublicShell>} />
          <Route path="/privacy-policy" element={<PublicShell><BlockRendererPage slug="privacy" testId="privacy-page" /></PublicShell>} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/terms-of-service" element={<PublicShell><BlockRendererPage slug="terms" testId="terms-page" /></PublicShell>} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/security" element={<PublicShell><BlockRendererPage slug="security" testId="security-page" /></PublicShell>} />
          <Route path="/soc2" element={<Navigate to="/security" replace />} />
          <Route path="/developers" element={<PublicShell><BlockRendererPage slug="developers" testId="api-docs-page" /></PublicShell>} />
          <Route path="/developer-docs" element={<Navigate to="/developers" replace />} />
          <Route path="/openhouse" element={<PublicShell><BlockRendererPage slug="openhouse" testId="openhouse-page" /></PublicShell>} />
          <Route path="/academy" element={<PublicShell><BlockRendererPage slug="academy" testId="academy-page" /></PublicShell>} />
          <Route path="/case-studies" element={<PublicShell><CaseStudiesPage /></PublicShell>} />
          <Route path="/case-studies/:slug" element={<PublicShell><CaseStudyDetailPage /></PublicShell>} />
          {/* Admin draft preview — unlisted, renders <slug>__draft in the public shell */}
          <Route path="/preview/:slug" element={<PublicShell><DraftPreviewPage /></PublicShell>} />
          {/* Legacy redirects */}
          <Route path="/request-a-demo" element={<Navigate to="/contact" replace />} />
          <Route path="/we-are-hiring" element={<Navigate to="/careers" replace />} />
          {/* Blog post direct slug routes (for SEO parity with live site) */}
          <Route path="/:slug" element={<PublicShell><CatchAllRoute /></PublicShell>} />
          {/* Final catch-all 404 */}
          <Route path="*" element={<PublicShell><NotFoundPage /></PublicShell>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
